import Bugsnag from "@bugsnag/js";
import { FunctionComponent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import AppStoreLink from "@Components/atoms/AppStoreLink";
import FooterInfos from "@Components/molecules/FooterInfos";
import useCheerzApiProvider from "@Hooks/useCheerzApiProvider";
import { ReactComponent as IconFsc } from "@Icons/fsc.svg";
import { ReactComponent as IconNeutralClimate } from "@Icons/neutral-climate.svg";
import enFlagLogo from "@Images/flag-en.png";
import esFlagLogo from "@Images/flag-es.png";
import frFlagLogo from "@Images/flag-fr.png";
import itFlagLogo from "@Images/flag-it.png";
import cheerzLogo from "@Images/logo-cheerz-white.png";
import { getPathChangingLocale, Locale, LOCALES } from "@Locales/index";
import { Currency } from "@Types/api/price";
import { FooterLink, SocialLinkByLocales } from "@Types/links";
import UserContent from "@Types/user";
import {
  commitmentsLinks,
  fscLinks,
  getConfidentialityLink,
  joinUsLink,
  neutralCarbonLink,
  originBlogLink,
  socialLinks,
} from "@Utils/links";
import { convertUserFromApiToContent } from "@Utils/user";
import "./style.scss";

interface Props {
  currency: Currency;
}

interface ContainerProps {
  userData?: UserContent;
}

export const Footer: FunctionComponent<Props> = ({ currency }) => {
  const intl = useIntl();

  const aboutLinks: FooterLink[] = [
    { name: "join", target: "_blank", url: joinUsLink },
    { name: "legals", target: "_self", url: `https://www.cheerz.com/${intl.locale}/legal` },
    {
      name: "terms",
      target: "_self",
      url: `https://www.cheerz.com/${intl.locale}/terms-and-conditions`,
    },
    {
      name: "confidentiality",
      target: "_blank",
      url: getConfidentialityLink(intl.locale),
    },
  ];
  const helpLinks: FooterLink[] = [
    {
      name: "order",
      target: "_self",
      url: `https://www.cheerz.com/${intl.locale}/orders`,
    },
    {
      name: "promo_code",
      target: "_self",
      url: `https://www.cheerz.com/${intl.locale}/categories/code-promo-cheerz`,
    },
    { name: "faq", target: "_blank", url: "http://support.cheerz.com/hc/" },
    {
      name: "contact",
      target: "_self",
      url: `https://www.cheerz.com/${intl.locale}/contact-us`,
    },
    {
      name: "partnership",
      target: "_self",
      url: `https://cheerz.com/partenariats`,
    },
    {
      name: "large_quantities",
      target: "_self",
      url: `https://www.cheerz.com/grandes-quantites`,
    },
  ];
  const productsLinks: FooterLink[] = [
    { name: "all", url: `https://www.cheerz.com/${intl.locale}/categories/all` },
    { name: "books", url: `https://www.cheerz.com/${intl.locale}/categories/books` },
    { name: "prints", url: `https://www.cheerz.com/${intl.locale}/categories/prints` },
    { name: "canvas", url: `https://www.cheerz.com/${intl.locale}/categories/canvas` },
    {
      name: "advent_calendar",
      url: `https://www.cheerz.com/${intl.locale}/categories/advent-calendar`,
    },
    {
      name: "cheap_books",
      url: `https://www.cheerz.com/${intl.locale}/products/cheap-photo-album`,
    },
    {
      name: "cheap_prints",
      url: `https://www.cheerz.com/${intl.locale}/products/cheap-photo-prints`,
    },
    {
      name: "cheap_poster",
      url: `https://www.cheerz.com/${intl.locale}/products/cheap-poster-framed`,
    },
    {
      name: "photobooth",
      url: "https://live.cheerz.com",
    },
  ];

  return (
    <div className="Footer">
      <FooterInfos currency={currency} />

      <div className="Footer-links-wrapper">
        <div className="Footer-links">
          <div className="Footer-links-products">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_products_title" })}
            </p>
            {productsLinks.map((link: FooterLink) => (
              <a className="Footer-links-link" href={link.url} key={link.name}>
                {intl.formatMessage({ id: `cheerz_Footer_links_products_${link.name}` })}
              </a>
            ))}
          </div>

          <div className="Footer-links-about">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_about_title" })}
            </p>
            {intl.locale === "fr" && (
              <a
                className="Footer-links-link"
                href={originBlogLink}
                rel="noopener noreferrer"
                target="_blank"
              >
                Le blog
              </a>
            )}
            <a className="Footer-links-link" href={commitmentsLinks[intl.locale]}>
              {intl.formatMessage({ id: "cheerz_Footer_links_about_commitments" })}
            </a>
            {aboutLinks.map((link: FooterLink) => (
              <a
                className="Footer-links-link"
                href={link.url}
                key={link.name}
                rel={link.target === "_blank" ? "noopener noreferrer" : ""}
                target={link.target}
              >
                {intl.formatMessage({ id: `cheerz_Footer_links_about_${link.name}` })}
              </a>
            ))}
          </div>

          <div className="Footer-links-help">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_help_title" })}
            </p>
            {helpLinks.map((link: FooterLink) => {
              let href = link.url;
              if (link.name === "faq") {
                href = `${link.url}${intl.locale}`;
              } else if (
                (link.name === "partnership" || link.name === "large_quantities") &&
                intl.locale !== "fr"
              ) {
                return null;
              }

              return (
                <a
                  className="Footer-links-link"
                  href={href}
                  key={link.name}
                  rel={link.target === "_blank" ? "noopener noreferrer" : ""}
                  target={link.target}
                >
                  {intl.formatMessage({ id: `cheerz_Footer_links_help_${link.name}` })}
                </a>
              );
            })}
          </div>

          <div className="Footer-links-commitments">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_commitments_title" })}
            </p>
            <a
              className="Footer-links-commitments-item"
              href={fscLinks[intl.locale]}
              rel="noopener noreferrer"
              target="_blank"
            >
              <IconFsc />
              <span>{intl.formatMessage({ id: "cheerz_Footer_links_commitments_fsc" })}</span>
            </a>
            <a
              className="Footer-links-commitments-item"
              href={neutralCarbonLink}
              rel="noopener noreferrer"
              target="_blank"
            >
              <IconNeutralClimate />
              <span>
                {intl.formatMessage({ id: "cheerz_Footer_links_commitments_neutral_climate" })}
              </span>
            </a>
          </div>

          <div className="Footer-links-connecting">
            <p className="Footer-links-title">
              {intl.formatMessage({ id: "cheerz_Footer_links_connecting_title" })}
            </p>
            <div className="Footer-links-connecting-wrapper">
              {socialLinks.map((socialLink: SocialLinkByLocales) => {
                const href =
                  socialLink.name === "Facebook"
                    ? socialLink.url[intl.locale]
                    : socialLink.name === "Twitter"
                    ? socialLink.url[intl.locale] || socialLink.url.world
                    : socialLink.url.world;

                return (
                  <a href={href} key={socialLink.name} rel="noopener noreferrer" target="_blank">
                    <img alt={socialLink.name} src={socialLink.src} title={socialLink.name} />
                  </a>
                );
              })}
            </div>
          </div>
          <div className="Footer-links-appStoreLinks">
            <AppStoreLink store="apple" />
            <AppStoreLink store="google" />
          </div>

          <div className="Footer-links-languages">
            {LOCALES.map((locale: Locale) => {
              const language = {
                en: { name: "English", src: enFlagLogo },
                es: { name: "Español", src: esFlagLogo },
                fr: { name: "Français", src: frFlagLogo },
                it: { name: "Italiano", src: itFlagLogo },
              };

              return (
                <a
                  className="Footer-links-link"
                  href={getPathChangingLocale(locale)}
                  key={language[locale].name}
                >
                  <img
                    alt={language[locale].name}
                    height="20"
                    src={language[locale].src}
                    title={language[locale].name}
                    width="20"
                  />
                  {language[locale].name}
                </a>
              );
            })}
          </div>

          <div className="Footer-links-logo">
            <img alt="Cheerz" src={cheerzLogo} />
          </div>

          <div className="Footer-links-copyright">Copyright {new Date().getFullYear()} Cheerz®</div>
        </div>
      </div>
    </div>
  );
};

const FooterContainer: FunctionComponent<ContainerProps> = ({ userData }) => {
  const [currency, setCurrency] = useState<Currency>("EUR");
  const cheerzApiProvider = useCheerzApiProvider();

  useEffect(() => {
    if (!!userData) {
      setCurrency(userData.currency);
    } else {
      cheerzApiProvider
        .getUser()
        .then((userResponse) => setCurrency(convertUserFromApiToContent(userResponse).currency))
        .catch((error: Error) => {
          Bugsnag.notify(error);
        });
    }
  }, [cheerzApiProvider]);

  return <Footer currency={currency}></Footer>;
};

export default FooterContainer;
